import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Text,
  FormControl,
  FormLabel,
  InputGroup,
  Input,
  InputRightElement,
  Flex,
} from '@chakra-ui/react';
import { BoundingBox } from '@phosphor-icons/react';
import * as S from './DimensionsModal.styles';
import { DimensionsModalProps, DimensionsProps } from './DimensionsModal.types';
import { useQuantitatives } from '../../../../contexts/QuantitativesContext';
import { CustomIcon } from '../../../CustomIcon/CustomIcon.component';
import { WarningAlertIcon } from '../../../Icons';

export function DimensionsModal({ isOpen, onClose, onSubmit }: DimensionsModalProps) {
  const { setMeasures, measures, setAlertModalIsOpen, alertModalIsOpen, isModalOpen } =
    useQuantitatives();
  const ref = useRef<HTMLDivElement>(null);

  const [dimensions, setDimensions] = useState<DimensionsProps>({
    largura: isModalOpen.addNewQuantitativo ? undefined : measures.largura,
    comprimento: isModalOpen.addNewQuantitativo ? undefined : measures.comprimento,
    disabled: !(!isModalOpen.addNewQuantitativo && measures.largura && measures.comprimento),
  });
  const [, forceRefreshPage] = useState(0);

  const handleChangeDimension = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, dimensionType: string) => {
      const updatedDimensions = { ...dimensions };
      const value = event.target.value ? Number(event.target.value) : undefined;

      const dimensionUpdatesByType = {
        largura: {
          ...updatedDimensions,
          largura: value,
          disabled: !(
            updatedDimensions.comprimento &&
            value &&
            updatedDimensions.comprimento >= 6 &&
            value >= 15 &&
            value <= 50
          ),
        },
        comprimento: {
          ...updatedDimensions,
          comprimento: value,
          disabled: !(
            updatedDimensions.largura &&
            value &&
            updatedDimensions.largura >= 15 &&
            updatedDimensions.largura <= 50 &&
            value >= 6
          ),
        },
      };

      const newDimensions = dimensionUpdatesByType[dimensionType];
      setDimensions(newDimensions);
    },
    [dimensions],
  );

  const areaGalpao = (dimensions.comprimento! * dimensions.largura!).toFixed(2);

  useLayoutEffect(() => {
    const boundies = ref?.current?.getBoundingClientRect();
    if (ref?.current && boundies?.width && dimensions.comprimento! > 0 && dimensions.largura! > 0) {
      forceRefreshPage(boundies.width);
    } else {
      setTimeout(() => forceRefreshPage(Math.random()), 0);
    }
  }, [dimensions, ref, isOpen]);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalRef.current) {
      if (alertModalIsOpen.open) {
        modalRef.current.style.top = '-200vh';
      } else {
        modalRef.current.removeAttribute('style');
      }
    }
  }, [alertModalIsOpen, modalRef]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent ref={modalRef}>
        <ModalHeader>
          <CustomIcon
            icon={BoundingBox}
            boxSize={6}
            color="#B3B3B3"
            $bgColor="#F2F4F7"
            $borderColor="#F9FAFB"
            $isRounded
          />
          <Text color="#1D1D1B" fontSize="18px" mt="1rem">
            Definir dimensões do galpão
          </Text>
          <Text fontWeight="400" color="#767B7B" fontSize="14px" mt="0.5rem">
            Insira o valor desejado nos campos abaixo
          </Text>
        </ModalHeader>
        <ModalBody alignItems="center">
          <Flex flexDirection="row" alignItems="end" gap="1rem" mb="1.5rem">
            <FormControl w="fit-content">
              <FormLabel display="flex">
                <Text color="#2D3748">Largura</Text>
              </FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  max={50}
                  step="0.01"
                  value={dimensions.largura}
                  placeholder=""
                  textAlign="end"
                  onChange={(event) => handleChangeDimension(event, 'largura')}
                />
                <InputRightElement>
                  <Text>
                    m <span style={{ color: '#C53030' }}>*</span>
                  </Text>
                </InputRightElement>
              </InputGroup>
              {dimensions.largura && dimensions.largura > 50 ? (
                <Text position="absolute" as="span" color="red" fontSize="10px">
                  O valor máximo é 50
                </Text>
              ) : null}
              {dimensions.largura && dimensions.largura < 15 ? (
                <Text position="absolute" as="span" color="red" fontSize="10px">
                  O valor mínimo é 15
                </Text>
              ) : null}
            </FormControl>
            <Text mb="0.5rem">X</Text>
            <FormControl w="fit-content">
              <FormLabel display="flex">
                <Text color="#2D3748">Comprimento</Text>
              </FormLabel>
              <InputGroup>
                <Input
                  type="number"                  
                  max={1000}
                  step="0.01"
                  value={dimensions.comprimento}
                  placeholder=""
                  textAlign="end"
                  onChange={(event) => handleChangeDimension(event, 'comprimento')}
                />
                <InputRightElement>
                  <Text>
                    m <span style={{ color: '#C53030' }}>*</span>
                  </Text>
                </InputRightElement>
              </InputGroup>
              {dimensions.comprimento && dimensions.comprimento < 6 ? (
                <Text position="absolute" as="span" color="red" fontSize="10px">
                  O valor mínimo é 6
                </Text>
              ) : null}
            </FormControl>
            <Text mb="0.5rem">=</Text>
            <FormControl w="fit-content">
              <FormLabel display="flex">
                <Text color="#2D3748">Área total</Text>
              </FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  placeholder=""
                  disabled
                  value={areaGalpao}
                  textAlign="end"
                  color="#2D3748"
                  opacity="unset !important"
                  padding="0px 29px 0px 0px"
                />
                <InputRightElement w="35px">m²</InputRightElement>
              </InputGroup>
            </FormControl>
          </Flex>

          {dimensions.largura && dimensions.comprimento ? (
            <S.ImageAreaRepresentation
              $largura={dimensions.largura}
              $comprimento={dimensions.comprimento}
              boundies={ref?.current?.getBoundingClientRect()}
              ref={ref}
            />
          ) : null}
        </ModalBody>

        <ModalFooter>
          <Button
            flex="1"
            variant="tertiary"
            mr={3}
            onClick={() => {
              setAlertModalIsOpen({
                open: true,
                submitDangerous: false,
                title: 'Modificações não salvas',
                description: 'Ao cancelar, você perderá todo o progresso realizado.',
                icon: WarningAlertIcon,
                textSubmit: 'Continuar',
                textCancel: 'Cancelar',
                onSubmit: () => {
                  onClose();
                },
              });
            }}
          >
            Cancelar
          </Button>
          <Button
            flex="1"
            variant="quaternary"
            isDisabled={dimensions.disabled}
            onClick={() => {
              setMeasures((prevState) => ({
                ...prevState,
                largura: dimensions.largura!,
                comprimento: dimensions.comprimento!,
              }));
              onSubmit();
            }}
          >
            Continuar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
